import React, { ChangeEventHandler, useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Conversations, WebSocketMessageType } from '../model';
import { fetchConversationByUuid, WS_API_CONVERSATION_URL } from '../../api/ConversationApi';
import { getAuthToken } from '../security/AuthContext';
import './ConversationPage.scss';
import Badge from '../list/Badge';
import ExpandableBlock from './ExpandableBlock';

const useConversationData = (uuid: string | undefined) => {
  const [conversation, setConversation] = useState<Conversations | null>(null);
  const [topic, setTopic] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!uuid) return;
    fetchConversationByUuid(uuid)
      .then(data => {
        setConversation(data);
        setIsLoaded(true);
        setTopic(data.topic);
      })
      .catch(() => setError('Failed to load conversation'));
  }, [uuid]);

  return { conversation, setConversation, topic, setTopic, error, isLoaded };
};

const useConversationSocket = (uuid: string | undefined, setConversation: React.Dispatch<React.SetStateAction<Conversations | null>>, setIsNewMessage: React.Dispatch<React.SetStateAction<boolean>>) => {
  const socketRef = useRef<WebSocket | null>(null);
  useEffect(() => {
    if (!uuid) return;
    const token = getAuthToken();
    const ws = new WebSocket(`${WS_API_CONVERSATION_URL}/${uuid}?token=${token}`);
    ws.onopen = () => {
      setIsNewMessage(false);
      socketRef.current = ws;
    };
    ws.onmessage = (event) => {
      const messageData = JSON.parse(event.data);
      if (messageData.type === WebSocketMessageType.MESSAGE) {
        const message = messageData.message;
        setConversation(prev => prev ? {
          ...prev,
          chatMessages: [...prev.chatMessages || [], message],
        } : prev);
        setIsNewMessage(true);
        setTimeout(() => setIsNewMessage(false), 1000);
      } else if (messageData.type === WebSocketMessageType.CONVERSATION) {
        const summary = messageData.message?.summary;
        setConversation(prev => prev ? {
          ...prev,
          summary,
        } : prev);
        setIsNewMessage(true);
        setTimeout(() => setIsNewMessage(false), 1000);
      }
    };
    ws.onclose = () => {
      socketRef.current = null;
    };
    return () => {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    };
  }, [uuid, setConversation, setIsNewMessage]);
  return socketRef;
};

const ConversationPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { conversation, setConversation, topic, setTopic, error } = useConversationData(uuid);
  const [newMessage, setNewMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isNewMessage, setIsNewMessage] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const socketRef = useConversationSocket(uuid, setConversation, setIsNewMessage);
  const [infoExpanded, setInfoExpanded] = useState(true);
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  useEffect(() => {
    setIsConnected(!!socketRef.current && socketRef.current.readyState === WebSocket.OPEN);
  }, [socketRef.current?.readyState]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [conversation?.chatMessages]);

  const handleSendMessage = useCallback(() => {
    const ws = socketRef.current;
    if (newMessage && ws?.readyState === WebSocket.OPEN) {
      ws.send(newMessage);
      setNewMessage('');
    }
  }, [newMessage]);

  const formatSummary = (text: string) => {
    return text.replace(/\*\*(.*?)\*\*/g, '<b>\n$1</b>');
  };

  const handleInputChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
    setNewMessage(event.target.value);
  };

  if (error) return <div>{error}</div>;

  return (
    <div className="conversation-page">
      <div>
        <Badge badgeTitle={isConnected ? 'CONNECTED' : 'DISCONNECTED'} />
        <div className="conversation-layout">
          <div className={`chat-container ${isNewMessage ? 'new-message' : ''}`}>
            <div className="chat-messages" ref={chatContainerRef}>
              {conversation?.chatMessages?.map(message => (
                <div
                  key={message.uuid}
                  className={`message ${message.senderType === 'HUMAN' ? 'right' : 'left'} ${message.senderType === 'AGENT' ? 'agent' : ''}`}
                >
                  <div className="message-body">{message.body}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="side-panels">
            <ExpandableBlock
              title="Metadata"
              expanded={infoExpanded}
              onToggle={() => setInfoExpanded(v => !v)}
              className="info-panel"
            >
              <p>Channel type: {conversation?.type}</p>
              {conversation?.metadata && (
                <div className="info-details">
                  <div>Failed attempts: {conversation.metadata.failedAttempts}</div>
                  <div>Sentiment: {conversation.metadata.sentiment ?? '-'}</div>
                </div>
              )}
            </ExpandableBlock>
            {conversation?.summary && (
              <ExpandableBlock
                title="Summary"
                expanded={summaryExpanded}
                onToggle={() => setSummaryExpanded(v => !v)}
                className="summary-panel"
              >
                <div className="conversation-summary__ai-text" dangerouslySetInnerHTML={{ __html: formatSummary(conversation.summary) }} />
              </ExpandableBlock>
            )}
          </div>
        </div>
        <div className="message-input">
          <textarea
            value={newMessage}
            onChange={handleInputChange}
            placeholder="Type your message..."
            className="message-input__textarea-input"
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />
          <button className="message-input__send-button" onClick={handleSendMessage}>
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversationPage;